// OptionAnalysisModal — extracted from App.js during the Trading Hub rebuild
// (M4). Pure relocation, no logic changes: TeaScoreCard, OrderPanel, and
// TradeConfirmModal are included here too since they're only ever used by
// OptionAnalysisModal (confirmed no other call sites in App.js) and were
// defined immediately above it.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API, fmtN, fmtPct } from "../lib/appCommon";
import { formatAiText } from "../lib/utils";

// ─── Tea Score Card ───────────────────────────────────────────────────────────
function TeaScoreCard({ symbol }) {
  const [tea, setTea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [fullNews, setFullNews] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tea/score?symbol=${symbol}`);
      setTea(r.data);
    } catch {} finally { setLoading(false); }
  };

  const loadFullNews = async () => {
    setShowPopup(true);
    try {
      const r = await axios.get(`${API}/tea/detail?symbol=${symbol}`);
      setFullNews(r.data);
    } catch {}
  };

  useEffect(() => { if (symbol) load(); }, [symbol]);

  // teaScore is null when the catalyst could not be graded. Without this
  // check every comparison below is false and an ungraded symbol renders in
  // the bearish red reserved for a score under 40.
  const teaGraded = tea?.teaScore != null;
  const scoreColor = !teaGraded ? "var(--muted)" : tea.teaScore >= 70 ? "var(--green)" : tea.teaScore >= 40 ? "var(--yellow)" : "var(--red)";
  const sentimentTag = !tea ? "t-neu" : tea.sentiment === "bullish" ? "t-bull" : tea.sentiment === "bearish" ? "t-bear" : "t-neu";

  return (
    <>
      <div className="card" style={{ marginBottom: 12, borderColor: "rgba(250,204,21,0.2)", background: "rgba(250,204,21,0.03)", cursor: "pointer" }} data-testid="tea-score" onClick={loadFullNews}>
        <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6 }}>Tea Score <span className="tag t-neu" style={{ fontSize: 8 }}>Tap for full news</span></div>
        {loading ? <div><span className="spin" /> Analyzing...</div> : tea ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: teaGraded ? 28 : 20, fontWeight: 700, color: scoreColor }}>{teaGraded ? tea.teaScore : "Not graded"}</div>
              <div>
                <span className={`tag ${sentimentTag}`} style={{ fontSize: 11 }}>{tea.sentiment?.toUpperCase()}</span>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{tea.summary}</div>
              </div>
            </div>
            {tea.headlines?.length > 0 && <div style={{ fontSize: 10, color: "var(--dim)" }}>{tea.headlines.slice(0, 2).map((h, i) => <div key={i}>&#8226; {h}</div>)}</div>}
          </div>
        ) : <div style={{ fontSize: 11, color: "var(--dim)" }}>No news data</div>}
      </div>

      {/* Tea Score Popup */}
      {showPopup && (
        <div style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
          onClick={e => e.target === e.currentTarget && setShowPopup(false)}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, maxWidth: 550, width: "100%", maxHeight: "80vh", overflowY: "auto", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 36, fontWeight: 700, color: scoreColor }}>{teaGraded ? tea.teaScore : "—"}</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Tea Score — {symbol}</div>
                  <span className={`tag ${sentimentTag}`}>{tea?.sentiment?.toUpperCase() || "LOADING"}</span>
                </div>
              </div>
              <button onClick={() => setShowPopup(false)} style={{ background: "var(--layer3)", border: "none", color: "var(--text)", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 16 }}>&times;</button>
            </div>
            {tea?.summary && <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16, lineHeight: 1.6 }}>{tea.summary}</div>}
            <div className="sh">Latest News Headlines</div>
            {(fullNews?.headlines || tea?.headlines || []).map((h, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: "0.5px solid var(--divider)", fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
                <span style={{ color: "var(--text)" }}>{h}</span>
              </div>
            ))}
            {!(fullNews?.headlines || tea?.headlines || []).length && <div style={{ color: "var(--dim)", fontSize: 12 }}>No headlines available</div>}
            <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 12 }}>Source: {tea?.source || fullNews?.source || "Google News + Omega ZI AI"}</div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Order Panel ──────────────────────────────────────────────────────────────
function OrderPanel({ option, mid }) {
  const [qty, setQty] = useState(1);
  const [limitPrice, setLimitPrice] = useState(mid?.toFixed(2) || "");
  const [tradeModal, setTradeModal] = useState(null); // {side: "BUY"|"SELL"|"ADD"}

  return (
    <>
      <div className="card" style={{ borderColor: "rgba(74,222,128,0.2)" }} data-testid="order-panel">
        <div className="sh">Trade This Contract</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <div><div className="m-lbl">Qty</div><input className="inp" type="number" value={qty} onChange={e => setQty(Number(e.target.value))} style={{ width: 60 }} min={1} data-testid="order-qty" /></div>
          <div><div className="m-lbl">Limit $</div><input className="inp" type="number" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} style={{ width: 80 }} step="0.01" data-testid="order-limit" /></div>
          <div style={{ display: "flex", gap: 6, marginTop: 18 }}>
            <button className="btn-buy" onClick={() => setTradeModal({ side: "BUY" })} data-testid="order-buy">
              Buy to Open
            </button>
            <button className="btn-sell" onClick={() => setTradeModal({ side: "SELL" })} data-testid="order-sell">
              Sell to Close
            </button>
            <button className="btn" onClick={() => setTradeModal({ side: "ADD" })} data-testid="order-add" style={{ fontSize: 11 }}>
              Add to Position
            </button>
          </div>
        </div>
      </div>
      {tradeModal && (
        <TradeConfirmModal
          option={option}
          mid={parseFloat(limitPrice) || mid}
          quantity={qty}
          side={tradeModal.side}
          onClose={() => setTradeModal(null)}
        />
      )}
    </>
  );
}

// ─── Trade Confirmation Modal ─────────────────────────────────────────────────
function TradeConfirmModal({ option, mid, quantity, side, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  useEffect(() => {
    loadAnalysis();
  }, []);

  const loadAnalysis = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/trade/analysis`, {
        symbol: option.symbol,
        strike: option.strike,
        optionType: option.type,
        expiry: option.expiry,
        side: side === "ADD" ? "BUY" : side,
        quantity,
        limitPrice: mid,
        underlyingPrice: option.underlying || 0,
        osi: option.osi,
        iv: option.iv || 0.5,
      });
      // The render below reads a.decision/.summary/.prediction unconditionally —
      // a response that resolves without throwing but omits one of those (a
      // partial backend failure) would otherwise crash the modal instead of
      // showing the existing error state.
      if (!r.data?.decision || !r.data?.summary || !r.data?.prediction) {
        setAnalysis({ error: "Incomplete analysis returned — try again." });
      } else {
        setAnalysis(r.data);
      }
    } catch (e) {
      setAnalysis({ error: e.message });
    }
    setLoading(false);
  };

  const executeOrder = async () => {
    setExecuting(true);
    try {
      const r = await axios.post(`${API}/order/place`, {
        symbol: option.symbol,
        instrumentType: option.osi ? "OPTION" : "EQUITY",
        osi: option.osi,
        side: side === "ADD" ? "BUY" : side,
        orderType: "LIMIT",
        quantity,
        limitPrice: mid,
        openClose: side === "SELL" ? "CLOSE" : "OPEN",
      });
      setOrderResult(r.data);
    } catch (e) {
      setOrderResult({ ok: false, error: e.message });
    }
    setExecuting(false);
  };

  const a = analysis;
  const isBuy = side === "BUY" || side === "ADD";

  return (
    <div data-testid="trade-confirm-modal" style={{ position: "fixed", inset: 0, zIndex: 150, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12, backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-3d" style={{ background: "var(--surface)", border: `2px solid ${isBuy ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`, borderRadius: 18, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 0 }}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "var(--surface)", zIndex: 2, borderRadius: "18px 18px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: isBuy ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: isBuy ? "#4ade80" : "#f87171" }}>
              {isBuy ? "B" : "S"}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{side === "ADD" ? "Add to Position" : `${side} to ${isBuy ? "Open" : "Close"}`}</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>{option.symbol} ${option.strike} {option.type?.toUpperCase()} — {option.expiry}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "var(--layer3)", border: "none", color: "var(--text)", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 16 }} data-testid="trade-modal-close">&times;</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <span className="spin" /> <span style={{ fontSize: 13, color: "var(--muted)", marginLeft: 8 }}>Analyzing trade...</span>
          </div>
        ) : a?.error ? (
          <div style={{ padding: 20, color: "var(--red)", fontSize: 12 }}>Analysis failed: {a.error}</div>
        ) : a && (
          <div style={{ padding: "16px 20px" }}>

            {/* Decision Indicator — Big hero badge */}
            <div data-testid="trade-decision" style={{ textAlign: "center", padding: "16px 0", marginBottom: 14 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "12px 24px", borderRadius: 14,
                background: `${a.decision.color}12`, border: `2px solid ${a.decision.color}40` }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${a.decision.color}25`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: a.decision.color }}>{a.decision.confidence.toFixed(0)}</span>
                </div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: a.decision.color, letterSpacing: 0.5 }}>{a.decision.verdict}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>Confidence: {a.decision.confidence}% | {a.decision.signalStrength} signal{a.decision.signalStrength !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>

            {/* 1. Trade Summary */}
            <div className="card" style={{ marginBottom: 12, borderColor: isBuy ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)" }} data-testid="trade-summary">
              <div className="sh" style={{ fontSize: 11 }}>Trade Summary</div>
              <div className="grid3" style={{ marginBottom: 6 }}>
                <div className="m-box"><div className="m-lbl">Quantity</div><div className="m-val" style={{ fontSize: 16 }}>{quantity} ct</div></div>
                <div className="m-box"><div className="m-lbl">Premium</div><div className="m-val" style={{ fontSize: 16 }}>${fmtN(mid, 2)}</div></div>
                <div className="m-box"><div className="m-lbl">{isBuy ? "Total Cost" : "Total Credit"}</div><div className={`m-val ${isBuy ? "neg" : "pos"}`} style={{ fontSize: 18 }}>{isBuy ? "-" : "+"}${fmtN(a.summary.totalCost, 0)}</div></div>
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 10, color: "var(--muted)", flexWrap: "wrap" }}>
                <span>Breakeven: <span style={{ fontWeight: 600, color: "var(--blue)" }}>${fmtN(a.summary.breakeven, 2)}</span></span>
                <span>Intrinsic: <span style={{ fontWeight: 600 }}>${fmtN(a.summary.intrinsic, 2)}</span></span>
                <span>Time Value: <span style={{ fontWeight: 600 }}>${fmtN(a.summary.timeValue, 2)}</span></span>
                <span>{a.summary.dte} DTE</span>
                <span>Max Loss: <span className="neg" style={{ fontWeight: 600 }}>{fmt$(a.summary.maxLoss)}</span></span>
              </div>
            </div>

            {/* 2. Prediction Engine */}
            <div className="card" style={{ marginBottom: 12 }} data-testid="trade-prediction">
              <div className="sh" style={{ fontSize: 11 }}>Prediction Engine</div>
              <div className="grid4">
                <div className="m-box"><div className="m-lbl">Prob. Profit</div><div className={`m-val ${a.prediction.probProfit > 50 ? "pos" : "neg"}`} style={{ fontSize: 18 }}>{a.prediction.probProfit}%</div></div>
                <div className="m-box"><div className="m-lbl">Fair Value</div><div className="m-val" style={{ fontSize: 16, color: a.prediction.mispriced ? "var(--green)" : "var(--muted)" }}>${fmtN(a.prediction.fairValue, 2)}{a.prediction.mispriced && <span style={{ fontSize: 9, marginLeft: 3 }}>!</span>}</div></div>
                <div className="m-box"><div className="m-lbl">Best Case</div><div className="m-val pos" style={{ fontSize: 14 }}>+{fmt$(a.prediction.bestCase)}</div></div>
                <div className="m-box"><div className="m-lbl">Worst Case</div><div className="m-val neg" style={{ fontSize: 14 }}>{fmt$(a.prediction.worstCase)}</div></div>
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>
                Price Target: <span style={{ fontWeight: 600 }}>${fmtN(a.prediction.priceTarget, 2)}</span> |
                95% Range: <span style={{ fontWeight: 500 }}>${fmtN(a.prediction.ci95?.[0], 2)} — ${fmtN(a.prediction.ci95?.[1], 2)}</span> |
                Expected P&L: <span className={a.prediction.expectedPnl >= 0 ? "pos" : "neg"} style={{ fontWeight: 600 }}>{a.prediction.expectedPnl >= 0 ? "+" : ""}{fmt$(a.prediction.expectedPnl)}</span>
              </div>
            </div>

            {/* 3. Signals & Risk Factors */}
            <div className="grid2" style={{ marginBottom: 12 }}>
              <div className="card" style={{ borderColor: "rgba(74,222,128,0.15)" }} data-testid="trade-signals">
                <div className="sh" style={{ fontSize: 11, color: "var(--green)" }}>Signals ({a.decision.signals.length})</div>
                {a.decision.signals.length > 0 ? a.decision.signals.map((s, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--muted)", padding: "3px 0", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", display: "inline-block" }} />
                    {s}
                  </div>
                )) : <div style={{ fontSize: 11, color: "var(--dim)" }}>No strong signals detected</div>}
              </div>
              <div className="card" style={{ borderColor: "rgba(248,113,113,0.15)" }} data-testid="trade-risks">
                <div className="sh" style={{ fontSize: 11, color: "var(--red)" }}>Risk Factors ({a.decision.risks.length})</div>
                {a.decision.risks.length > 0 ? a.decision.risks.map((r, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--muted)", padding: "3px 0", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--red)", display: "inline-block" }} />
                    {r}
                  </div>
                )) : <div style={{ fontSize: 11, color: "var(--dim)" }}>No major risks identified</div>}
              </div>
            </div>

            {/* 4. Position Impact & Risk Checks */}
            <div className="card" style={{ marginBottom: 12, borderColor: a.riskChecks.allClear ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.2)" }} data-testid="trade-risk-checks">
              <div className="sh" style={{ fontSize: 11 }}>Position Impact & Risk Checks</div>
              <div className="grid4">
                <div className="m-box"><div className="m-lbl">Portfolio %</div><div className={`m-val ${a.riskChecks.riskPct < 3 ? "pos" : a.riskChecks.riskPct < 6 ? "" : "neg"}`} style={{ fontSize: 16 }}>{a.riskChecks.riskPct}%</div></div>
                <div className="m-box"><div className="m-lbl">Buying Power</div><div className="m-val" style={{ fontSize: 14 }}>{fmt$(a.riskChecks.buyingPower)}</div></div>
                <div className="m-box"><div className="m-lbl">PDT</div><div className="m-val" style={{ fontSize: 14, color: a.riskChecks.pdtOk ? "var(--green)" : "var(--red)" }}>{a.riskChecks.dayTradesUsed}/3</div></div>
                <div className="m-box"><div className="m-lbl">Risk Gate</div><div className="m-val" style={{ fontSize: 14, color: a.riskChecks.allClear ? "var(--green)" : "var(--red)", fontWeight: 700 }}>{a.riskChecks.allClear ? "CLEAR" : "WARN"}</div></div>
              </div>
            </div>

            {/* 5. Greeks snapshot */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="sh" style={{ fontSize: 11 }}>Greeks</div>
              <div className="grid5">
                <div className="ph"><div className="ph-l">Delta</div><div style={{ fontSize: 13, fontWeight: 500, color: "var(--green)" }}>{fmtN(a.greeks.delta, 3)}</div></div>
                <div className="ph"><div className="ph-l">Gamma</div><div style={{ fontSize: 13, fontWeight: 500, color: "var(--blue)" }}>{fmtN(a.greeks.gamma, 4)}</div></div>
                <div className="ph"><div className="ph-l">Theta</div><div style={{ fontSize: 13, fontWeight: 500, color: "var(--red)" }}>{fmtN(a.greeks.theta, 4)}</div></div>
                <div className="ph"><div className="ph-l">Vega</div><div style={{ fontSize: 13, fontWeight: 500, color: "var(--purple)" }}>{fmtN(a.greeks.vega, 4)}</div></div>
                <div className="ph"><div className="ph-l">IV</div><div style={{ fontSize: 13, fontWeight: 500, color: "var(--yellow)" }}>{(a.greeks.iv * 100).toFixed(1)}%</div></div>
              </div>
            </div>

            {/* Execution Area */}
            {orderResult ? (
              <div style={{ padding: 14, borderRadius: 12, background: orderResult.ok ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", border: `1px solid ${orderResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`, textAlign: "center" }}
                data-testid="trade-order-result">
                <div style={{ fontSize: 14, fontWeight: 700, color: orderResult.ok ? "var(--green)" : "var(--red)", marginBottom: 4 }}>
                  {orderResult.ok ? "Order Placed Successfully" : "Order Failed"}
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{orderResult.ok ? `${side} ${quantity} x ${option.symbol} $${option.strike} ${option.type?.toUpperCase()}` : orderResult.error}</div>
                <button className="btn" style={{ marginTop: 10 }} onClick={onClose}>Close</button>
              </div>
            ) : !confirmed ? (
              <div style={{ display: "flex", gap: 10 }} data-testid="trade-confirm-area">
                <button className={isBuy ? "btn-buy" : "btn-sell"} style={{ flex: 1, padding: "14px 0", fontSize: 14, fontWeight: 700, borderRadius: 12 }}
                  onClick={() => setConfirmed(true)} data-testid="trade-confirm-btn">
                  Confirm {side === "ADD" ? "Add" : side} — {fmt$(a.summary.totalCost)}
                </button>
                <button className="btn" style={{ padding: "14px 20px", borderRadius: 12 }} onClick={onClose}>Cancel</button>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "10px 0" }} data-testid="trade-execute-area">
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Are you sure? This will send a live order to your broker.</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button className={isBuy ? "btn-buy" : "btn-sell"} style={{ padding: "12px 32px", fontSize: 13, fontWeight: 700, borderRadius: 10 }}
                    onClick={executeOrder} disabled={executing} data-testid="trade-execute-btn">
                    {executing ? "Executing..." : `Execute ${side}`}
                  </button>
                  <button className="btn" onClick={() => setConfirmed(false)} style={{ borderRadius: 10 }}>Go Back</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Option Analysis Modal (Full Deep-Dive) ──────────────────────────────────
function OptionAnalysisModal({ option, onClose }) {
  const [greeks, setGreeks] = useState(null);
  const [bsData, setBsData] = useState(null);
  const [mcData, setMcData] = useState(null);
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plScenarios, setPlScenarios] = useState([]);
  const [predChart, setPredChart] = useState(null);
  const [deepDive, setDeepDive] = useState(false);
  const [plChart, setPlChart] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [deepAi, setDeepAi] = useState("");
  const [deepAiLoading, setDeepAiLoading] = useState(false);
  const [whatIfPrice, setWhatIfPrice] = useState(null);
  const [whatIfDte, setWhatIfDte] = useState(null);

  const S = option.underlying || 0;
  const K = option.strike || 0;
  const mid = option.bid != null && option.ask != null ? (option.bid + option.ask) / 2 : option.last || 0;
  const daysToExpiry = option.expiry ? Math.max(1, Math.round((new Date(option.expiry) - new Date()) / 86400000)) : 30;
  const T = daysToExpiry / 365;

  useEffect(() => {
    loadAnalysis();
  }, []);

  const loadAnalysis = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      option.osi ? axios.get(`${API}/market/greeks?osi=${option.osi}`) : Promise.reject("no osi"),
      axios.post(`${API}/zi/black-scholes`, { S, K, T, sigma: 0.5, type: option.type }),
      axios.post(`${API}/zi/monte-carlo`, { S, K, T, sigma: 0.5, nPaths: 10000 }),
      axios.get(`${API}/market/ohlcv?symbol=${option.symbol}&days=60`),
    ]);

    if (results[0].status === "fulfilled") {
      const g = results[0].value.data;
      setGreeks(g);
      const realIv = g.iv || 0.5;
      try {
        const bsRes = await axios.post(`${API}/zi/black-scholes`, { S, K, T, sigma: realIv, type: option.type });
        setBsData(bsRes.data);
        const mcRes = await axios.post(`${API}/zi/monte-carlo`, { S, K, T, sigma: realIv, nPaths: 10000 });
        setMcData(mcRes.data);
      } catch {
        if (results[1].status === "fulfilled") setBsData(results[1].value.data);
        if (results[2].status === "fulfilled") setMcData(results[2].value.data);
      }
    } else {
      if (results[1].status === "fulfilled") setBsData(results[1].value.data);
      if (results[2].status === "fulfilled") setMcData(results[2].value.data);
    }

    // Build P&L scenarios
    const scenarios = [];
    const entryPremium = mid;
    const moves = [-20, -15, -10, -5, 0, 5, 10, 15, 20];
    moves.forEach(pct => {
      const futurePrice = S * (1 + pct / 100);
      let intrinsic;
      if (option.type === "call") intrinsic = Math.max(0, futurePrice - K);
      else intrinsic = Math.max(0, K - futurePrice);
      const pnl = (intrinsic - entryPremium) * 100;
      scenarios.push({ move: pct, price: futurePrice, intrinsic, pnl, pnlPct: entryPremium > 0 ? ((intrinsic - entryPremium) / entryPremium * 100) : 0 });
    });
    setPlScenarios(scenarios);

    // Build interactive P&L payoff curve
    buildPayoffChart(entryPremium);

    // Price prediction chart
    if (results[3].status === "fulfilled") {
      const bars = results[3].value.data?.bars || [];
      if (bars.length > 5) {
        const labels = bars.slice(-30).map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
        const closes = bars.slice(-30).map(b => b.c);
        const last5 = closes.slice(-5);
        const trend = (last5[last5.length - 1] - last5[0]) / 5;
        const projLabels = [];
        const projData = [...closes.map(() => null)];
        projData[projData.length - 1] = closes[closes.length - 1];
        for (let d = 1; d <= daysToExpiry && d <= 15; d++) {
          projLabels.push(`+${d}d`);
          projData.push(closes[closes.length - 1] + trend * d);
        }
        setPredChart({
          labels: [...labels, ...projLabels],
          datasets: [
            { label: option.symbol, data: [...closes, ...projLabels.map(() => null)], borderColor: "#60a5fa", pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
            { label: "Projection", data: projData, borderColor: "rgba(250,204,21,0.7)", borderDash: [4, 4], pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
            { label: `Strike $${K}`, data: [...labels, ...projLabels].map(() => K), borderColor: "rgba(248,113,113,0.4)", borderDash: [2, 6], pointRadius: 0, borderWidth: 1 },
          ],
        });
      }
    }

    setLoading(false);
    loadAiInsight();
  };

  const buildPayoffChart = (entryPremium) => {
    const pts = 80;
    const lo = S * 0.7;
    const hi = S * 1.3;
    const step = (hi - lo) / pts;
    const labels = [];
    const pnlData = [];
    const colors = [];
    const breakeven = option.type === "call" ? K + entryPremium : K - entryPremium;

    for (let i = 0; i <= pts; i++) {
      const px = lo + step * i;
      labels.push(`$${px.toFixed(0)}`);
      let intrinsic;
      if (option.type === "call") intrinsic = Math.max(0, px - K);
      else intrinsic = Math.max(0, K - px);
      const pnl = (intrinsic - entryPremium) * 100;
      pnlData.push(pnl);
      colors.push(pnl >= 0 ? "rgba(74,222,128,0.8)" : "rgba(248,113,113,0.8)");
    }

    setPlChart({
      labels,
      datasets: [{
        label: "P&L at Expiry ($)",
        data: pnlData,
        borderColor: pnlData.map(v => v >= 0 ? "#4ade80" : "#f87171"),
        backgroundColor: pnlData.map(v => v >= 0 ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)"),
        segment: { borderColor: ctx => (ctx.p1.parsed.y >= 0 ? "#4ade80" : "#f87171") },
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.1,
      }],
      breakeven,
    });

    // Build P&L heatmap: rows = DTE (time), cols = price levels
    const dteSteps = [1, 3, 7, 14, 21, daysToExpiry];
    const priceSteps = 11;
    const pLo = S * 0.85;
    const pHi = S * 1.15;
    const pStep = (pHi - pLo) / (priceSteps - 1);
    const hm = { dtes: [], prices: [], grid: [] };
    for (let pi = 0; pi < priceSteps; pi++) hm.prices.push(pLo + pStep * pi);
    for (const dte of dteSteps) {
      if (dte > daysToExpiry) continue;
      hm.dtes.push(dte);
      const row = [];
      const tFrac = dte / 365;
      for (const px of hm.prices) {
        // Simple BS approximation for option value at different DTE/price
        const iv = (greeks?.iv || 0.5);
        const d1 = (Math.log(px / K) + (0.05 + iv * iv / 2) * tFrac) / (iv * Math.sqrt(tFrac + 0.001));
        const nd1 = 0.5 * (1 + erf(d1 / Math.sqrt(2)));
        const d2 = d1 - iv * Math.sqrt(tFrac + 0.001);
        const nd2 = 0.5 * (1 + erf(d2 / Math.sqrt(2)));
        let optVal;
        if (option.type === "call") {
          optVal = px * nd1 - K * Math.exp(-0.05 * tFrac) * nd2;
        } else {
          optVal = K * Math.exp(-0.05 * tFrac) * (1 - nd2) - px * (1 - nd1);
        }
        const pnl = (Math.max(0, optVal) - entryPremium) * 100;
        row.push(Math.round(pnl));
      }
      hm.grid.push(row);
    }
    setHeatmapData(hm);
  };

  // Error function approximation for BS
  const erf = (x) => {
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    return sign * y;
  };

  const loadAiInsight = async () => {
    setAiLoading(true);
    try {
      const ziData = bsData && mcData ? `Zi System Assessment: MC PoP=${(mcData.probProfit * 100).toFixed(1)}%, Fair Value=$${bsData.price?.toFixed(2)}, ` : "";
      const greekStr = greeks ? `Greeks: Delta=${greeks.delta}, Gamma=${greeks.gamma}, Theta=${greeks.theta}, IV=${greeks.iv ? (greeks.iv * 100).toFixed(1) + '%' : 'N/A'}` : '';

      const r = await axios.post(`${API}/zi/chat`, {
        message: `Analyze this option contract (under 120 words). Be CONSISTENT with the quantitative data provided — do not contradict the numbers:

${option.symbol} ${option.type.toUpperCase()} $${K} strike, expiring ${option.expiry}
Underlying: $${S}, Bid: $${option.bid}, Ask: $${option.ask}, Volume: ${option.volume}, OI: ${option.oi}
${greekStr}
${ziData}
${mcData ? `Monte Carlo (10K paths): ${(mcData.probProfit * 100).toFixed(1)}% probability of profit, target $${mcData.priceTarget}` : ''}

IMPORTANT: If MC probability of profit is above 50%, this is a FAVORABLE setup. If Tea Score is bullish and MC is positive, give a BUY or HOLD recommendation. Only say AVOID if MC PoP is below 30% AND the contract has clear structural problems.

Give a BUY / HOLD / AVOID verdict with 2-3 key reasons. Be specific with entry/exit targets.`,
        sessionId: `opt-analysis-${Date.now()}`
      });
      setAiInsight(r.data?.response || "");
    } catch { setAiInsight("AI analysis unavailable."); }
    finally { setAiLoading(false); }
  };

  const loadDeepDiveAi = async () => {
    if (deepAi) return;
    setDeepAiLoading(true);
    try {
      const breakeven = option.type === "call" ? K + mid : K - mid;
      const isCall = option.type === "call";
      const intrinsic = isCall ? Math.max(0, S - K) : Math.max(0, K - S);
      const timeValue = mid - intrinsic;
      const r = await axios.post(`${API}/zi/chat`, {
        message: `Deep P&L analysis for ${option.symbol} ${option.type.toUpperCase()} $${K} strike, exp ${option.expiry} (${daysToExpiry} DTE):

Current: Stock $${S.toFixed(2)}, Mid $${mid.toFixed(2)}, Intrinsic $${intrinsic.toFixed(2)}, Time Value $${timeValue.toFixed(2)}
Breakeven: $${breakeven.toFixed(2)} (${((breakeven/S - 1)*100).toFixed(1)}% move needed)
${greeks ? `IV: ${greeks.iv ? (greeks.iv*100).toFixed(1)+'%' : 'N/A'}, Delta: ${greeks.delta}, Theta: ${greeks.theta}/day ($${(Math.abs(greeks.theta||0)*100).toFixed(0)}/contract/day)` : ''}
${mcData ? `MC: ${(mcData.probProfit*100).toFixed(1)}% PoP, Target: $${mcData.priceTarget}` : ''}

Provide a structured analysis:
1. **Why This Could WIN** — 3 specific catalysts or scenarios
2. **Why This Could LOSE** — 3 specific risks (theta decay rate, IV crush scenarios, directional risk)
3. **Breakeven Analysis** — How much the stock needs to move and by when
4. **Time Decay Impact** — Day-by-day theta bleed projection for next 5 days
5. **Optimal Exit Strategy** — When to take profit, when to cut loss
6. **Projection** — Price target and probability within DTE window

Be specific with numbers. Think like a risk manager.`,
        sessionId: `deep-dive-${Date.now()}`
      });
      setDeepAi(r.data?.response || "");
    } catch { setDeepAi("Deep analysis unavailable."); }
    finally { setDeepAiLoading(false); }
  };

  const openDeepDive = () => {
    setDeepDive(true);
    loadDeepDiveAi();
  };

  const itm = option.type === "call" ? K <= S : K >= S;
  const fairValue = bsData?.price || 0;
  const mispriced = mid > 0 && fairValue > 0 && Math.abs(fairValue - mid) / mid > 0.1;

  // Computed risk metrics
  const breakeven = option.type === "call" ? K + mid : K - mid;
  const maxLoss = mid * 100;
  const isCall = option.type === "call";
  const intrinsicVal = isCall ? Math.max(0, S - K) : Math.max(0, K - S);
  const timeVal = Math.max(0, mid - intrinsicVal);
  const riskReward = mcData && mid > 0 ? ((mcData.priceTarget - K) / mid).toFixed(1) : "—";
  const moveNeeded = S > 0 ? (((breakeven / S) - 1) * 100).toFixed(1) : "0";
  const thetaDaily = greeks?.theta ? Math.abs(greeks.theta * 100) : 0;

  const miniChartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, labels: { color: "var(--muted)", boxWidth: 8, font: { size: 9 } } }, tooltip: { mode: "index", intersect: false, backgroundColor: "var(--surface)" } },
    scales: { x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 8 }, grid: { color: "var(--grid)" } }, y: { ticks: { color: "var(--dim)", font: { size: 8 } }, grid: { color: "var(--grid)" } } },
  };

  const payoffChartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index", intersect: false, backgroundColor: "var(--surface)",
        callbacks: { label: (ctx) => `P&L: ${ctx.parsed.y >= 0 ? '+' : ''}$${ctx.parsed.y.toFixed(0)}` }
      }
    },
    scales: {
      x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 10 }, grid: { color: "var(--grid)" } },
      y: { ticks: { color: "var(--dim)", font: { size: 9 }, callback: v => `$${v}` }, grid: { color: "var(--grid)" } }
    },
  };

  // Escapes real HTML before applying the bold/italic markdown transform —
  // see lib/utils.js's formatAiText docstring for why this matters (this
  // text is AI/market-data-derived, and the old version had no escaping at
  // all before dangerouslySetInnerHTML).
  const formatAi = formatAiText;

  // Helper to get heatmap cell color
  const hmColor = (val) => {
    if (val > 500) return "rgba(74,222,128,0.7)";
    if (val > 200) return "rgba(74,222,128,0.5)";
    if (val > 50) return "rgba(74,222,128,0.25)";
    if (val > 0) return "rgba(74,222,128,0.12)";
    if (val > -50) return "rgba(248,113,113,0.12)";
    if (val > -200) return "rgba(248,113,113,0.25)";
    return "rgba(248,113,113,0.5)";
  };

  return (
    <div data-testid="option-modal" style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-3d" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, maxWidth: deepDive ? 900 : 700, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 0, transition: "max-width 0.4s cubic-bezier(0.22,1,0.36,1)" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "var(--surface)", zIndex: 2, borderRadius: "16px 16px 0 0" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{option.symbol} ${K} {option.type.toUpperCase()}</span>
              <span className={`tag ${option.type === "call" ? "t-bull" : "t-bear"}`}>{option.type.toUpperCase()}</span>
              {itm && <span className="tag t-bull" style={{ fontSize: 9 }}>ITM</span>}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>Exp: {option.expiry} | {daysToExpiry} DTE | Source: Public.com</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {deepDive && <button className="btn" onClick={() => setDeepDive(false)} style={{ fontSize: 10 }} data-testid="back-overview">Back to Overview</button>}
            <button
              onClick={onClose}
              data-testid="close-modal"
              aria-label="Close"
              style={{
                width: 36, height: 36, borderRadius: 18,
                background: "var(--layer3)", border: "1px solid var(--border)",
                color: "var(--text)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </div>

        <div style={{ padding: "16px 20px" }}>

          {/* ═══ DEEP DIVE P&L MODE ═══ */}
          {deepDive ? (
            <div className="deep-dive-enter" data-testid="deep-dive-panel">

              {/* Risk/Reward Summary Bar */}
              <div className="card dd-card" style={{ marginBottom: 14, background: "linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(74,222,128,0.04) 100%)", borderColor: "rgba(59,130,246,0.2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>P&L Deep Dive</span>
                  <span className="tag t-blue" style={{ fontSize: 8 }}>COMPREHENSIVE</span>
                </div>
                <div className="grid5" data-testid="risk-reward-summary">
                  <div className="m-box dd-metric"><div className="m-lbl">Max Loss</div><div className="m-val neg" style={{ fontSize: 18 }}>-{fmt$(maxLoss)}</div><div style={{ fontSize: 9, color: "var(--dim)" }}>100% of premium</div></div>
                  <div className="m-box dd-metric"><div className="m-lbl">Breakeven</div><div className="m-val" style={{ fontSize: 18, color: "var(--blue)" }}>${fmtN(breakeven, 2)}</div><div style={{ fontSize: 9, color: "var(--dim)" }}>{moveNeeded > 0 ? "+" : ""}{moveNeeded}% move</div></div>
                  <div className="m-box dd-metric"><div className="m-lbl">Risk/Reward</div><div className="m-val" style={{ fontSize: 18, color: parseFloat(riskReward) > 2 ? "var(--green)" : "var(--yellow)" }}>{riskReward}x</div><div style={{ fontSize: 9, color: "var(--dim)" }}>target/cost</div></div>
                  <div className="m-box dd-metric"><div className="m-lbl">Theta Bleed</div><div className="m-val neg" style={{ fontSize: 18 }}>-${fmtN(thetaDaily, 0)}/day</div><div style={{ fontSize: 9, color: "var(--dim)" }}>${fmtN(thetaDaily * 5, 0)} in 5 days</div></div>
                  <div className="m-box dd-metric"><div className="m-lbl">Prob. Profit</div><div className={`m-val ${mcData && mcData.probProfit > 0.5 ? "pos" : "neg"}`} style={{ fontSize: 18 }}>{mcData ? fmtPct(mcData.probProfit * 100) : "—"}</div><div style={{ fontSize: 9, color: "var(--dim)" }}>Monte Carlo 10K</div></div>
                </div>
              </div>

              {/* Payoff Curve Chart */}
              {plChart && (
                <div className="card dd-card" style={{ marginBottom: 14 }}>
                  <div className="sh">Payoff Curve at Expiry</div>
                  <div style={{ height: 220, position: "relative" }}>
                    <Line data={plChart} options={payoffChartOpts} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 8 }}>
                    <span>Breakeven: <span style={{ color: "var(--blue)", fontWeight: 600 }}>${fmtN(breakeven, 2)}</span></span>
                    <span>Max Loss: <span className="neg" style={{ fontWeight: 600 }}>{fmt$(maxLoss)}</span></span>
                    <span>Unlimited upside {isCall ? "above" : "below"} ${fmtN(breakeven, 2)}</span>
                  </div>
                </div>
              )}

              {/* P&L Heatmap: Price × Time */}
              {heatmapData && heatmapData.grid.length > 0 && (
                <div className="card dd-card" style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div className="sh" style={{ margin: 0 }}>P&L Heatmap (Price x Time)</div>
                    <span style={{ fontSize: 9, color: "var(--dim)" }}>Green = profit / Red = loss (per contract)</span>
                  </div>
                  <div style={{ overflowX: "auto" }} data-testid="pnl-heatmap">
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "4px 6px", fontSize: 9, color: "var(--dim)", textAlign: "left" }}>DTE</th>
                          {heatmapData.prices.map((p, i) => (
                            <th key={i} style={{ padding: "4px 2px", fontSize: 9, color: Math.abs(p - S) < (S * 0.01) ? "var(--blue)" : "var(--dim)", fontWeight: Math.abs(p - S) < (S * 0.01) ? 700 : 400 }}>
                              ${p.toFixed(0)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {heatmapData.grid.map((row, ri) => (
                          <tr key={ri}>
                            <td style={{ padding: "6px 6px", fontSize: 10, fontWeight: 600, color: "var(--muted)" }}>{heatmapData.dtes[ri]}d</td>
                            {row.map((val, ci) => (
                              <td key={ci} style={{
                                padding: "6px 2px", textAlign: "center", fontWeight: 600, fontSize: 9,
                                background: hmColor(val), color: val >= 0 ? "#4ade80" : "#f87171",
                                borderRadius: 3, transition: "all 0.2s"
                              }}>
                                {val >= 0 ? "+" : ""}{val}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* P&L Scenarios Table */}
              {plScenarios.length > 0 && (
                <div className="card dd-card" style={{ marginBottom: 14 }}>
                  <div className="sh">P&L Scenarios at Expiry</div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="tbl">
                      <thead><tr><th style={{ textAlign: "left" }}>Move</th><th>Stock Price</th><th>Intrinsic</th><th>P&L (1 contract)</th><th>Return</th></tr></thead>
                      <tbody>
                        {plScenarios.map((s, i) => (
                          <tr key={i} style={{ background: s.pnl >= 0 ? "rgba(74,222,128,0.04)" : "rgba(248,113,113,0.04)" }}>
                            <td style={{ textAlign: "left", fontWeight: 500 }}>{s.move >= 0 ? "+" : ""}{s.move}%</td>
                            <td>${fmtN(s.price, 2)}</td>
                            <td>${fmtN(s.intrinsic, 2)}</td>
                            <td className={s.pnl >= 0 ? "pos" : "neg"} style={{ fontWeight: 600 }}>{s.pnl >= 0 ? "+" : ""}{fmt$(s.pnl)}</td>
                            <td className={s.pnlPct >= 0 ? "pos" : "neg"}>{s.pnlPct >= 0 ? "+" : ""}{fmtN(s.pnlPct, 1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 6 }}>Max loss: {fmt$(maxLoss)} (100% of premium) | Breakeven: ${fmtN(breakeven, 2)}</div>
                </div>
              )}

              {/* What-If Simulator */}
              <div className="card dd-card" style={{ marginBottom: 14, borderColor: "rgba(167,139,250,0.25)", background: "rgba(167,139,250,0.03)" }} data-testid="what-if-simulator">
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <span className="sh" style={{ margin: 0 }}>What-If Simulator</span>
                  <span className="tag" style={{ fontSize: 8, background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>INTERACTIVE</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}>Drag the sliders to see how P&L, Greeks, and probability change in real-time.</div>
                <div className="grid2" style={{ gap: 20, marginBottom: 14 }}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 500 }}>Stock Price</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--blue)" }}>${(whatIfPrice ?? S).toFixed(2)}</span>
                    </div>
                    <input type="range" min={Math.round(S * 0.7)} max={Math.round(S * 1.3)} step={0.5}
                      value={whatIfPrice ?? S} onChange={e => setWhatIfPrice(parseFloat(e.target.value))}
                      style={{ width: "100%", accentColor: "#3b82f6" }} data-testid="whatif-price-slider" />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--dim)" }}>
                      <span>${(S * 0.7).toFixed(0)}</span><span>Current: ${S.toFixed(2)}</span><span>${(S * 1.3).toFixed(0)}</span>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 500 }}>Days to Expiry</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#a78bfa" }}>{whatIfDte ?? daysToExpiry}d</span>
                    </div>
                    <input type="range" min={1} max={daysToExpiry} step={1}
                      value={whatIfDte ?? daysToExpiry} onChange={e => setWhatIfDte(parseInt(e.target.value))}
                      style={{ width: "100%", accentColor: "#a78bfa" }} data-testid="whatif-dte-slider" />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--dim)" }}>
                      <span>1d</span><span>Current: {daysToExpiry}d</span><span>{daysToExpiry}d</span>
                    </div>
                  </div>
                </div>
                {/* Computed What-If Results */}
                {(() => {
                  const wP = whatIfPrice ?? S;
                  const wD = whatIfDte ?? daysToExpiry;
                  const wT = wD / 365;
                  const iv = greeks?.iv || 0.5;
                  // BS approximation
                  const d1 = (Math.log(wP / K) + (0.05 + iv * iv / 2) * wT) / (iv * Math.sqrt(wT + 0.001));
                  const nd1 = 0.5 * (1 + erf(d1 / Math.sqrt(2)));
                  const d2 = d1 - iv * Math.sqrt(wT + 0.001);
                  const nd2 = 0.5 * (1 + erf(d2 / Math.sqrt(2)));
                  let optVal;
                  if (isCall) optVal = wP * nd1 - K * Math.exp(-0.05 * wT) * nd2;
                  else optVal = K * Math.exp(-0.05 * wT) * (1 - nd2) - wP * (1 - nd1);
                  optVal = Math.max(0, optVal);
                  const wPnl = (optVal - mid) * 100;
                  const wPnlPct = mid > 0 ? ((optVal - mid) / mid * 100) : 0;
                  const wDelta = isCall ? nd1 : nd1 - 1;
                  const wTheta = -(wP * iv * Math.exp(-d1 * d1 / 2) / (2 * Math.sqrt(2 * Math.PI * (wT + 0.001)))) / 365;
                  const moveFromCurrent = ((wP / S) - 1) * 100;
                  // Simple PoP estimation
                  const wPoP = isCall ? (1 - nd2) * 100 : nd2 * 100;

                  return (
                    <div className="grid5" data-testid="whatif-results">
                      <div className="m-box dd-metric"><div className="m-lbl">Option Value</div><div className="m-val" style={{ fontSize: 16, color: "var(--blue)" }}>${fmtN(optVal, 2)}</div></div>
                      <div className="m-box dd-metric"><div className="m-lbl">P&L</div><div className={`m-val ${wPnl >= 0 ? "pos" : "neg"}`} style={{ fontSize: 16 }}>{wPnl >= 0 ? "+" : ""}{fmt$(wPnl)}</div></div>
                      <div className="m-box dd-metric"><div className="m-lbl">Return</div><div className={`m-val ${wPnlPct >= 0 ? "pos" : "neg"}`} style={{ fontSize: 16 }}>{wPnlPct >= 0 ? "+" : ""}{fmtN(wPnlPct, 1)}%</div></div>
                      <div className="m-box dd-metric"><div className="m-lbl">Delta</div><div className="m-val" style={{ fontSize: 16, color: "var(--green)" }}>{fmtN(wDelta, 3)}</div></div>
                      <div className="m-box dd-metric"><div className="m-lbl">Stock Move</div><div className={`m-val ${moveFromCurrent >= 0 ? "pos" : "neg"}`} style={{ fontSize: 16 }}>{moveFromCurrent >= 0 ? "+" : ""}{fmtN(moveFromCurrent, 1)}%</div></div>
                    </div>
                  );
                })()}
              </div>

              {/* AI Deep P&L Analysis */}
              <div className="card dd-card" style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.04)" }} data-testid="deep-ai-analysis">
                <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6 }}>Omega ZI AI Deep Analysis <span className="tag t-blue" style={{ fontSize: 8 }}>P&L FOCUS</span></div>
                {deepAiLoading ? (
                  <div style={{ fontSize: 12, color: "var(--muted)", padding: "12px 0" }}><span className="spin" /> Generating comprehensive P&L analysis...</div>
                ) : deepAi ? (
                  <div style={{ fontSize: 12, lineHeight: 1.8, color: "var(--muted)", whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: formatAi(deepAi) }} />
                ) : (
                  <div style={{ fontSize: 12, color: "var(--dim)" }}>Loading deep analysis...</div>
                )}
              </div>
            </div>

          ) : (
            /* ═══ STANDARD OVERVIEW MODE ═══ */
            <>
              {/* Live Price Row */}
              <div className="grid4" style={{ marginBottom: 14 }}>
                <div className="m-box"><div className="m-lbl">Underlying</div><div className="m-val" style={{ fontSize: 18 }}>${fmtN(S, 2)}</div></div>
                <div className="m-box"><div className="m-lbl">Bid / Ask</div><div className="m-val" style={{ fontSize: 16 }}><span style={{ color: "var(--green)" }}>${fmtN(option.bid, 2)}</span> / <span style={{ color: "var(--red)" }}>${fmtN(option.ask, 2)}</span></div></div>
                <div className="m-box"><div className="m-lbl">Mid Price</div><div className="m-val" style={{ fontSize: 18 }}>${fmtN(mid, 2)}</div></div>
                <div className="m-box"><div className="m-lbl">Fair Value (BS)</div><div className={`m-val ${mispriced ? "pos" : ""}`} style={{ fontSize: 18 }}>{loading ? "..." : `$${fmtN(fairValue, 2)}`}{mispriced && <span style={{ fontSize: 10, marginLeft: 4 }}>!</span>}</div></div>
              </div>

              {/* ITM / OTM Visual Gauge — CLICKABLE for Deep Dive */}
              {S > 0 && K > 0 && (() => {
                const intrinsic = isCall ? Math.max(0, S - K) : Math.max(0, K - S);
                const isItm = intrinsic > 0;
                const dist = S - K;
                const distPct = ((dist / S) * 100);
                const absPct = Math.abs(distPct);
                const lo = Math.min(S, K) * 0.92;
                const hi = Math.max(S, K) * 1.08;
                const range = hi - lo;
                const stockPos = ((S - lo) / range) * 100;
                const strikePos = ((K - lo) / range) * 100;
                const bePos = ((breakeven - lo) / range) * 100;
                const gaugeColor = isItm ? (isCall ? "#4ade80" : "#f87171") : "#facc15";
                const fillLeft = Math.min(stockPos, strikePos);
                const fillWidth = Math.abs(stockPos - strikePos);

                return (
                  <div className="card moneyness-card" style={{ marginBottom: 12, cursor: "pointer", transition: "all 0.3s" }}
                    data-testid="itm-gauge" onClick={openDeepDive}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="sh" style={{ margin: 0 }}>Moneyness</div>
                        <span className="dd-badge" data-testid="deep-dive-trigger">CLICK FOR P&L DEEP DIVE</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className={`tag ${isItm ? (isCall ? "t-bull" : "t-bear") : "t-neu"}`} style={{ fontSize: 11, fontWeight: 600 }}>
                          {isItm ? `ITM $${intrinsic.toFixed(2)}` : `OTM $${Math.abs(dist).toFixed(2)}`}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--muted)" }}>({absPct.toFixed(1)}% {isItm ? "in" : "out of"} the money)</span>
                      </div>
                    </div>

                    {/* Visual gauge bar */}
                    <div style={{ position: "relative", height: 44, background: "var(--layer2)", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: `${fillLeft}%`, width: `${fillWidth}%`, background: isItm ? "rgba(74,222,128,0.15)" : "rgba(250,204,21,0.12)", transition: "all 0.3s" }} />
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: `${Math.max(1, Math.min(99, strikePos))}%`, width: 2, background: "#f87171", zIndex: 2 }}>
                        <div style={{ position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)", fontSize: 9, color: "#f87171", fontWeight: 600, whiteSpace: "nowrap" }}>Strike ${K}</div>
                      </div>
                      <div style={{ position: "absolute", top: 4, left: `${Math.max(2, Math.min(96, stockPos))}%`, transform: "translateX(-50%)", zIndex: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: gaugeColor, border: "2px solid var(--surface)", boxShadow: `0 0 8px ${gaugeColor}` }} />
                        <div style={{ fontSize: 10, fontWeight: 700, color: gaugeColor, marginTop: 2, whiteSpace: "nowrap" }}>${S.toFixed(2)}</div>
                      </div>
                      {bePos > 0 && bePos < 100 && (
                        <div style={{ position: "absolute", top: 0, bottom: 0, left: `${Math.max(1, Math.min(99, bePos))}%`, width: 1, background: "rgba(96,165,250,0.5)", borderStyle: "dashed", zIndex: 1 }}>
                          <div style={{ position: "absolute", bottom: -16, left: "50%", transform: "translateX(-50%)", fontSize: 8, color: "var(--blue)", whiteSpace: "nowrap" }}>BE ${breakeven.toFixed(2)}</div>
                        </div>
                      )}
                    </div>

                    {/* Quick P&L preview row */}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "var(--muted)", marginBottom: 6 }}>
                      <span>Intrinsic: <span style={{ fontWeight: 600 }}>${intrinsic.toFixed(2)}</span></span>
                      <span>Time Value: <span style={{ fontWeight: 600 }}>${timeVal.toFixed(2)}</span></span>
                      <span>Max Loss: <span className="neg" style={{ fontWeight: 600 }}>{fmt$(maxLoss)}</span></span>
                      <span>Break: <span style={{ color: "var(--blue)", fontWeight: 600 }}>${fmtN(breakeven, 2)}</span></span>
                      <span>Theta: <span className="neg" style={{ fontWeight: 600 }}>-${fmtN(thetaDaily, 0)}/day</span></span>
                    </div>

                    <div style={{ display: "flex", gap: 16, fontSize: 10, color: "var(--muted)" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: gaugeColor, display: "inline-block" }} /> Stock ${S.toFixed(2)}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 2, background: "#f87171", display: "inline-block" }} /> Strike ${K}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 1, background: "var(--blue)", display: "inline-block", borderTop: "1px dashed var(--blue)" }} /> Breakeven ${breakeven.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Greeks */}
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="sh">Greeks (Public.com Live)</div>
                {greeks ? (
                  <div className="grid5">
                    <div className="ph"><div className="ph-l">Delta</div><div style={{ fontSize: 16, fontWeight: 500, color: "var(--green)" }}>{fmtN(greeks.delta, 4)}</div></div>
                    <div className="ph"><div className="ph-l">Gamma</div><div style={{ fontSize: 16, fontWeight: 500, color: "var(--blue)" }}>{fmtN(greeks.gamma, 5)}</div></div>
                    <div className="ph"><div className="ph-l">Theta</div><div style={{ fontSize: 16, fontWeight: 500, color: "var(--red)" }}>{fmtN(greeks.theta, 4)}</div></div>
                    <div className="ph"><div className="ph-l">Vega</div><div style={{ fontSize: 16, fontWeight: 500, color: "var(--purple)" }}>{fmtN(greeks.vega, 4)}</div></div>
                    <div className="ph"><div className="ph-l">IV</div><div style={{ fontSize: 16, fontWeight: 500, color: "var(--yellow)" }}>{greeks.iv ? `${(greeks.iv * 100).toFixed(1)}%` : "—"}</div></div>
                  </div>
                ) : loading ? <div><span className="spin" /> Loading Greeks...</div> : <div style={{ color: "var(--dim)", fontSize: 11 }}>Greeks unavailable for this contract</div>}
              </div>

              {/* Monte Carlo */}
              {mcData && (
                <div className="card" style={{ marginBottom: 12 }}>
                  <div className="sh">Monte Carlo Prediction (10,000 GBM paths)</div>
                  <div className="grid4">
                    <div className="m-box"><div className="m-lbl">Prob. of Profit</div><div className={`m-val ${mcData.probProfit > 0.5 ? "pos" : "neg"}`} style={{ fontSize: 20 }}>{fmtPct(mcData.probProfit * 100)}</div></div>
                    <div className="m-box"><div className="m-lbl">Price Target (60th)</div><div className="m-val" style={{ fontSize: 16 }}>${fmtN(mcData.priceTarget, 2)}</div></div>
                    <div className="m-box"><div className="m-lbl">95% CI Low</div><div className="m-val neg" style={{ fontSize: 14 }}>${fmtN(mcData.ci95?.[0], 2)}</div></div>
                    <div className="m-box"><div className="m-lbl">95% CI High</div><div className="m-val pos" style={{ fontSize: 14 }}>${fmtN(mcData.ci95?.[1], 2)}</div></div>
                  </div>
                </div>
              )}

              {/* Price + Prediction Chart */}
              {predChart && (
                <div className="card" style={{ marginBottom: 12 }}>
                  <div className="sh">Price + Projection</div>
                  <div style={{ height: 180 }}><Line data={predChart} options={miniChartOpts} /></div>
                </div>
              )}

              {/* Contract Stats */}
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="sh">Contract Stats</div>
                <div className="grid3">
                  <div className="ph"><div className="ph-l">Volume</div><div style={{ fontSize: 14, fontWeight: 500 }}>{(option.volume || 0).toLocaleString()}</div></div>
                  <div className="ph"><div className="ph-l">Open Interest</div><div style={{ fontSize: 14, fontWeight: 500 }}>{(option.oi || 0).toLocaleString()}</div></div>
                  <div className="ph"><div className="ph-l">Vol/OI Ratio</div><div style={{ fontSize: 14, fontWeight: 500 }}>{option.oi > 0 ? fmtN((option.volume || 0) / option.oi, 2) + "x" : "—"}</div></div>
                </div>
              </div>

              {/* Tea Score */}
              <TeaScoreCard symbol={option.symbol} />

              {/* AI Insight */}
              <div className="card" style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.04)" }}>
                <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6 }}>Omega ZI AI Insight <span className="tag t-blue" style={{ fontSize: 8 }}>v6.0</span></div>
                {aiLoading ? (
                  <div style={{ fontSize: 12, color: "var(--muted)" }}><span className="spin" /> Analyzing contract...</div>
                ) : aiInsight ? (
                  <div style={{ fontSize: 12, lineHeight: 1.7, color: "var(--muted)", whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: formatAi(aiInsight) }} />
                ) : (
                  <div style={{ fontSize: 12, color: "var(--dim)" }}>Waiting for AI analysis...</div>
                )}
              </div>

              {/* Order Placement */}
              <OrderPanel option={option} mid={mid} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OptionAnalysisModal;
